import type { AuthScope as WorldsAuthScope } from '@/client/services/worlds/authScopes/types'

const USER_AUTH_ROLES_SIMPLE = [
  'user:basic',
  'user:blocked',
  'user:blocked:community',
  // If account approval is required, and the user hasn't been reviewed yet
  'user:unapproved',
  // If the user was reviewed, but we need more information from them
  'user:rejected',
  'user:approved',
  'user:marketing_team',
  'user:founding_tutor',
  'user:ai:pilot',
  'user:recording:opt_out',
  'admin:tutor_operations:lead',
  'user:impact_fellowship:candidate',
  'user:tutor',
  'user:zoom:activated',
  'user:tutor:revoked',
] as const

const USER_FEATURE_FLAG_ROLES = ['user:feature_flag:ai-guide'] as const

export const USER_ROLES_AGE_ASSURANCE = [
  'user:age_assurance:waived',
  'user:age_assurance:assured',
  'user:age_assurance:failed',
] as const

const USER_AUTH_ROLES_ADMIN = [
  'admin:basic',
  'admin:certifications',
  'admin:certifications:lead',
  'admin:moderators:lead',
  'admin:policy',
  'admin:safety',
  'admin:mentor',
  'admin:super',
  'admin:team_lead',
  'admin:collegeboard',
  'admin:intern',
  'admin:key_club',
  'admin:blog',
  'admin:curriculum',
  'admin:user-support:specialist',
  'admin:user-support:lead',
  'admin:teach_for_america',
  'admin:federal_work_study',
] as const

export const USER_ROLES_BUCKET = [
  'bucket:13-to-under-16',
  'bucket:16-to-under-18',
  'bucket:18-to-under-21',
  'bucket:21-plus',
] as const

const USER_FLAG_ROLES = [
  // Current policy: (Anees / open to change)
  // Temporary flags -- meant to only apply to existing users in backfilling scenarios and such.
  // These roles must NEVER be used for cases where new users could qualify for them
  'user:flag:2025-05-background-check-missing',
] as const

export const USER_AUTH_ROLES = [
  ...USER_AUTH_ROLES_SIMPLE,
  ...USER_AUTH_ROLES_ADMIN,
  ...USER_ROLES_BUCKET,
  ...USER_FLAG_ROLES,
  ...USER_FEATURE_FLAG_ROLES,
  ...USER_ROLES_AGE_ASSURANCE,
] as const
type UserAuthRole = (typeof USER_AUTH_ROLES)[number]
// ^ Not exporting because downstream consumers should import from './types'

export const ASSIGNABLE_USER_ROLES: UserAuthRole[] = [
  'user:marketing_team',
  'user:ai:pilot',
  'admin:certifications',
  'admin:certifications:lead',
  'admin:moderators:lead',
  'admin:policy',
  'admin:safety',
  'admin:mentor',
  'admin:team_lead',
  'admin:collegeboard',
  'admin:teach_for_america',
  'admin:tutor_operations:lead',
  'admin:intern',
  'admin:key_club',
  'user:recording:opt_out',
  'admin:blog',
  'admin:curriculum',
  'admin:federal_work_study',
  ...USER_FEATURE_FLAG_ROLES,
]
type AssignableUserRole = (typeof ASSIGNABLE_USER_ROLES)[number]
// ^ Not exporting because downstream consumers should import from './types'

export const ROLE_TO_ASSIGNABLE_USER_ROLES: Partial<Record<UserAuthRole, AssignableUserRole[]>> = {
  'admin:super': ASSIGNABLE_USER_ROLES,
  'admin:team_lead': ['admin:certifications'],
}

export const USER_AUTH_SCOPES = [
  'user:avatar:photo',
  'user:ai:sat_bootcamp',
  'series:update',
  'admin:series_or_session:update',
  'admin:session:delete',
  'admin:sessioncomment:delete',
  'ops:marketing:referrals',
  'ops:users:block',
  'ops:users:reject',
  'admin:users:read',
  'admin:users:block',
  'admin:users:approve',
  'admin:users:unblock',
  'admin:users:delete',
  'admin:users:note:create',
  'admin:users:research_group:assign',
  'admin:users:research_group:view',
  'admin:users:college:review',
  'admin:volunteer-hours:review',
  'admin:certifications:manage',
  'admin:certifications:recordings:approve',
  'admin:certifications:all-stats:view',
  'admin:assessments:modify',
  'admin:assessments:view',
  'admin:assessments:release',
  'admin:messages:modify',
  'admin:messages:group:read',
  'admin:tutors:privilege:revoke',
  'admin:tutors:privilege:enact',
  'admin:session:view-admin-page',
  'admin:session:peer-review-from-admin-page',
  'admin:session:view-recordings',
  'admin:session:join-without-signup',
  'admin:series:view-admin-page',
  'admin:series:members:view',
  'admin:series:delete',
  'admin:series:edit-tags',
  'admin:series:research_group:assign',
  'admin:gating_group:assign',
  'admin:posts:read',
  'admin:posts:unhide',
  'admin:templates:manage',
  'admin:content-filters:modify',
  'admin:content-filters:view',
  'admin:world:manage-users',
  'admin:posts:announcements:create',
  'admin:posts:edit',
  'admin:posts:pin',
  'admin:posts:delete',
  'admin:post-events:edit',
  'admin:post-events:delete',
  'admin:posts:pin',
  'admin:phone_number:waive',
] as const

type UserAuthScope = (typeof USER_AUTH_SCOPES)[number]
// ^ Not exporting because downstream consumers should import from './types'

// Here we define which role inherits all of the roles from the roles below
export const USER_ROLES_HIERARCHY: Record<UserAuthRole, UserAuthRole[]> = {
  'user:basic': [],
  // ^ user:basic is the default for an authenticated user
  // Unauthenticated request context will lack it
  'user:blocked': [],
  'user:rejected': [],
  'user:impact_fellowship:candidate': [],
  'user:blocked:community': ['user:blocked'],
  'user:unapproved': [],
  'user:approved': [],
  'user:founding_tutor': [],
  'user:marketing_team': [],
  'user:tutor': ['user:approved'],
  'user:zoom:activated': [],
  'user:tutor:revoked': [],
  'user:ai:pilot': [],
  'user:recording:opt_out': [],
  'user:age_assurance:assured': [],
  'user:age_assurance:failed': [],
  'user:age_assurance:waived': [],
  'user:flag:2025-05-background-check-missing': [],
  'admin:basic': ['user:approved'],
  'admin:curriculum': ['admin:basic'],
  'admin:collegeboard': ['admin:basic'],
  'admin:teach_for_america': ['admin:basic'],
  'admin:team_lead': ['admin:basic'],
  'admin:user-support:specialist': ['admin:certifications'],
  'admin:user-support:lead': ['admin:team_lead', 'admin:user-support:specialist'],
  'admin:certifications': ['admin:basic'],
  'admin:certifications:lead': ['admin:certifications', 'admin:team_lead'],
  'admin:tutor_operations:lead': ['admin:basic', 'admin:team_lead'],
  'admin:moderators:lead': ['admin:basic', 'admin:team_lead'],
  'admin:mentor': ['admin:basic', 'admin:certifications'],
  'admin:safety': ['admin:policy'],
  'admin:policy': ['admin:basic'],
  'admin:intern': ['admin:basic'],
  'admin:key_club': ['admin:basic'],
  'admin:blog': ['admin:basic'],
  'admin:federal_work_study': ['admin:basic'],
  'admin:super': [
    'user:tutor',
    'user:ai:pilot',
    'admin:basic',
    'admin:certifications',
    'admin:certifications:lead',
    'admin:moderators:lead',
    'admin:mentor',
    'admin:policy',
    'admin:safety',
    'admin:tutor_operations:lead',
    'admin:team_lead',
    'admin:user-support:specialist',
    'admin:user-support:lead',
    'admin:intern',
    'admin:key_club',
    'admin:blog',
    'admin:collegeboard',
    'admin:federal_work_study',
  ],
  // Bucket roles are not inherited
  'bucket:13-to-under-16': [],
  'bucket:16-to-under-18': [],
  'bucket:18-to-under-21': [],
  'bucket:21-plus': [],

  // Feature flag roles are not inherited
  'user:feature_flag:ai-guide': [],
}

export const ROLES_TO_AUTH_SCOPES: Record<UserAuthRole, (UserAuthScope | WorldsAuthScope)[]> = {
  'user:basic': [],
  'user:approved': [],
  'user:unapproved': [],
  'user:blocked': [],
  'user:blocked:community': [],
  'user:rejected': [],
  'user:founding_tutor': [],
  'user:tutor': ['series:update', 'user:avatar:photo', 'ops:users:block', 'ops:users:reject'],
  'user:zoom:activated': [],
  'user:tutor:revoked': [],
  'user:marketing_team': ['ops:marketing:referrals'],
  'user:ai:pilot': ['user:ai:sat_bootcamp'],
  'user:recording:opt_out': [],
  'user:age_assurance:assured': [],
  'user:age_assurance:failed': [],
  'user:age_assurance:waived': [],
  'user:flag:2025-05-background-check-missing': [],
  'admin:collegeboard': [
    'admin:assessments:modify',
    'admin:assessments:view',
    'admin:series:members:view',
    'admin:session:view-admin-page',
    'admin:session:peer-review-from-admin-page',
    'admin:session:view-recordings',
    'admin:phone_number:waive',
  ],
  'admin:teach_for_america': [
    'admin:assessments:modify',
    'admin:assessments:release',
    'admin:assessments:view',
    'admin:series:members:view',
    'admin:series:view-admin-page',
    'admin:session:view-admin-page',
    'admin:session:peer-review-from-admin-page',
    'admin:session:view-recordings',
    'admin:session:join-without-signup',
    'admin:templates:manage',
  ],
  'user:impact_fellowship:candidate': [],
  'admin:curriculum': ['admin:assessments:modify'],
  'admin:tutor_operations:lead': [
    'admin:users:read',
    'admin:users:note:create',
    'admin:assessments:view',
  ],
  'admin:moderators:lead': [
    'admin:posts:announcements:create',
    'admin:series:edit-tags',
    'admin:series:view-admin-page',
  ],
  'admin:basic': [],
  'admin:blog': [],
  'admin:certifications': ['admin:certifications:manage'],
  'admin:certifications:lead': ['admin:certifications:all-stats:view'],
  'admin:mentor': [
    'admin:posts:read',
    'admin:tutors:privilege:revoke',
    'admin:tutors:privilege:enact',
    'admin:messages:group:read',
    'admin:session:view-admin-page',
    'admin:series:view-admin-page',
    'admin:session:peer-review-from-admin-page',
    'admin:session:view-recordings',
  ],
  'admin:safety': [
    'admin:users:read',
    'admin:users:note:create',
    'admin:users:block',
    'admin:users:approve',
    'admin:sessioncomment:delete',
    'admin:users:unblock',
    'admin:messages:group:read',
    'admin:posts:read',
    'admin:posts:unhide',
    'admin:content-filters:modify',
    'admin:content-filters:view',
  ],
  'admin:policy': [
    'admin:sessioncomment:delete',
    'admin:messages:modify',
    'admin:messages:group:read',
    'admin:series:members:view',
    'admin:series:view-admin-page',
    'admin:posts:read',
    'admin:session:view-admin-page',
    'admin:content-filters:view',

    // World auth scopes
    'world:posts:edit',
    'world:posts:delete',
    'world:post_events:edit',
    'world:post_events:delete',
  ],
  'admin:intern': [
    'admin:users:read',
    'world:posts:edit',
    'world:posts:delete',
    'world:post_events:edit',
    'world:post_events:delete',
    'world:posts:pin',
    'world:announcements:create',
    'admin:templates:manage',
  ],
  'admin:super': [
    'admin:users:delete',
    'admin:series_or_session:update',
    'admin:session:delete',
    'admin:sessioncomment:delete',
    'ops:marketing:referrals',
    'admin:assessments:modify',
    'admin:assessments:view',
    'admin:assessments:release',
    'admin:series:delete',
    'admin:series:edit-tags',
    'admin:users:research_group:assign',
    'admin:users:research_group:view',
    'admin:series:research_group:assign',
    'admin:certifications:recordings:approve',
    'admin:gating_group:assign',
    'admin:templates:manage',
    'admin:session:view-admin-page',
    'admin:series:view-admin-page',
    'admin:session:peer-review-from-admin-page',
    'admin:session:view-recordings',
    'admin:session:join-without-signup',
    'admin:certifications:manage',

    // World auth scopes
    'world:posts:create',
    'world:sessions:host',
    'world:tutor-application:review',
    'world:tutor-application:submit',
    'world:channel:description:edit',
    'world:details:edit',
    'world:posts:edit',
    'world:posts:delete',
    'world:post_events:edit',
    'world:post_events:delete',
    'world:posts:pin',
    'world:announcements:create',
    'world:moderate',
    'world:channel:text_notifications:receive',
    'world:posts:channel:edit',
    'admin:world:manage-users',

    // Global discussion posts
    'admin:posts:announcements:create',
    'admin:posts:edit',
    'admin:posts:delete',
    'admin:post-events:edit',
    'admin:post-events:delete',
    'admin:posts:pin',
  ],
  'admin:team_lead': ['admin:volunteer-hours:review'],
  'admin:key_club': [],
  'admin:user-support:specialist': [
    'admin:users:read',
    'admin:phone_number:waive',
    'admin:certifications:manage',
    'admin:session:view-admin-page',
    'admin:series:view-admin-page',
    'admin:volunteer-hours:review',
  ],
  'admin:user-support:lead': ['admin:series_or_session:update', 'admin:users:research_group:view'],
  'admin:federal_work_study': ['admin:users:college:review', 'admin:volunteer-hours:review'],

  // Bucket roles grant no auth scopes
  'bucket:13-to-under-16': [],
  'bucket:16-to-under-18': [],
  'bucket:18-to-under-21': [],
  'bucket:21-plus': [],

  // Feature flag roles grant no auth scopes
  'user:feature_flag:ai-guide': [],
}
